import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  BRAND_DEFAULTS,
  applyTerminology,
  hasTerminology,
  supportedLanguages,
  type Language,
  type OrgType,
} from '@up2d8/contracts';
import { buildMessages } from './resources';

const STORAGE_KEY = 'up2d8.language';

/**
 * Taalkeuze. De MVP ondersteunt nl, fr en en; de taal van een medewerker komt later uit
 * zijn profiel (Stap 3) en de vertaling van berichten uit de translation-module (Stap 11).
 */
export function detectLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (supportedLanguages as readonly string[]).includes(stored)) {
      return stored as Language;
    }
  } catch {
    // Privémodus of geblokkeerde opslag: dan gewoon de browsertaal.
  }
  const browser = navigator.language.slice(0, 2);
  return (supportedLanguages as readonly string[]).includes(browser) ? (browser as Language) : 'nl';
}

export function setLanguage(language: Language): void {
  void i18next.changeLanguage(language);
  document.documentElement.lang = language;
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Niet kunnen opslaan mag de taalwissel niet blokkeren.
  }
}

/**
 * Het type organisatie van wie aangemeld is (ORG-2, ADR-0087).
 *
 * Alle teksten zijn geschreven voor een productiebedrijf. Is de organisatie een vereniging, dan
 * zet deze nabewerking elke vertaalde tekst om ("de medewerker" wordt "het lid"), met dezelfde tabel
 * als de mails van de server. Zonder aanmelding, en in het platformbeheer, blijft het `factory`.
 */
let activeOrgType: OrgType = 'factory';

const terminology = {
  type: 'postProcessor' as const,
  name: 'terminology',
  process(
    value: string,
    _key: string | string[],
    _options: unknown,
    translator: { language?: string },
  ) {
    if (!hasTerminology(activeOrgType)) return value;
    return applyTerminology(value, activeOrgType, translator.language ?? i18next.language);
  },
};

/** Zet het type; de schermen tekenen opnieuw met de nieuwe woorden. */
export function setOrgType(orgType: OrgType): void {
  if (orgType === activeOrgType) return;
  activeOrgType = orgType;
  // react-i18next tekent opnieuw bij `languageChanged`; de taal zelf blijft dezelfde.
  i18next.emit('languageChanged', i18next.language);
}

export async function initI18n(): Promise<void> {
  const language = detectLanguage();
  // De hoofdbestanden plus `locales/beheer/*.json` (BHR-0); zie `resources.ts`.
  const messages = buildMessages();
  await i18next
    .use(initReactI18next)
    .use(terminology)
    .init({
      resources: {
        nl: { translation: messages.nl },
        fr: { translation: messages.fr },
        en: { translation: messages.en },
      },
      lng: language,
      fallbackLng: 'nl',
      interpolation: {
        escapeValue: false,
        // `{{product}}` staat in elke tekst die het product noemt; zie `setProductName`.
        defaultVariables: { product: BRAND_DEFAULTS.productName },
      },
      postProcess: ['terminology'],
    });
  document.documentElement.lang = language;
}

/**
 * De productnaam in elke tekst (`{{product}}`), na MRK: het platform kan het product een andere
 * naam geven.
 *
 * Een standaardvariabele en geen parameter per aanroep: tientallen teksten noemen het product, en
 * een aanroep die de naam vergeet, toont dan letterlijk "{{product}}". Zo kan dat niet. De schil
 * roept dit aan zodra het merk bekend is (`shared/theme.ts`); daarna laat `changeLanguage` naar
 * dezelfde taal elke vertaalde component opnieuw renderen.
 */
export function setProductName(name: string): void {
  const interpolation = i18next.options.interpolation;
  if (!interpolation) return;
  const variables = (interpolation.defaultVariables ??= {}) as Record<string, string>;
  if (variables.product === name) return;
  variables.product = name;
  if (i18next.isInitialized) void i18next.changeLanguage(i18next.language);
}

export { i18next };
